/** React Query hooks — one per endpoint in docs/api-contract.md. */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { z } from "zod";
import { api, apiVoid, ApiError, SLOW_MS } from "./client";
import * as S from "./schemas";

const REFRESH = 15 * 60 * 1000;

export const keys = {
  me: ["me"] as const,
  status: ["status"] as const,
  fitness: (days: number) => ["fitness", days] as const,
  weeks: (from?: string, to?: string) => ["weeks", from ?? "", to ?? ""] as const,
  activities: (p: object) => ["activities", p] as const,
  activity: (id: string) => ["activity", id] as const,
  plan: (from?: string, to?: string) => ["plan", from ?? "", to ?? ""] as const,
  phases: ["phases"] as const,
  adjustments: ["adjustments"] as const,
  constraints: ["constraints"] as const,
  availability: ["availability"] as const,
  goals: ["goals"] as const,
  routes: (day: string) => ["routes", day] as const,
  route: (id: string) => ["route", id] as const,
  places: ["places"] as const,
  health: (kind: string, days: number) => ["health", kind, days] as const,
  connections: ["connections"] as const,
  tokens: ["tokens"] as const,
  catalog: ["catalog"] as const,
  recommendations: ["recommendations"] as const,
  settings: ["settings"] as const,
  indoor: (day: string) => ["indoor", day] as const,
  heartRate: (day: string) => ["health", "hr", day] as const,
  mcp: ["mcp"] as const,
  storage: ["storage"] as const,
  amrFitness: (days: number) => ["amr-fitness", days] as const,
  racePractice: ["race-practice"] as const,
  skipReasons: ["skip-reasons"] as const,
  routeRequest: (day: string) => ["routes", "request", day] as const,
  uploads: ["uploads"] as const,
};

const useQ = <T>(key: QueryKey, fn: () => Promise<T>, enabled = true) =>
  useQuery({ queryKey: key, queryFn: fn, enabled, refetchInterval: REFRESH, staleTime: 60_000 });
/** A query keyed by a range the rider switches (RD-16, T5): the previous range's data stays on screen
 * (`isPlaceholderData`) while the next one loads, so charts don't drop back to a skeleton. */
const useRangeQ = <T>(key: QueryKey, fn: () => Promise<T>, enabled = true) =>
  useQuery({
    queryKey: key,
    queryFn: fn,
    enabled,
    refetchInterval: REFRESH,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

export const useMe = () => useQ(keys.me, () => api("/me", S.Me));
export const useStatus = () => useQ(keys.status, () => api("/status", S.Status));
export const useFitness = (days = 180) =>
  useRangeQ(keys.fitness(days), () => api("/fitness", z.array(S.FitnessPoint), { params: { days } }));
/** `enabled: false` waits (e.g. Progress waits for the server's today before it asks for a window, RD-15a). */
export const useWeeks = (from?: string, to?: string, enabled = true) =>
  useRangeQ(keys.weeks(from, to), () => api("/weeks", z.array(S.Week), { params: { from, to } }), enabled);
export const useActivities = (p: {
  limit?: number;
  offset?: number;
  sport?: string;
  from?: string;
  to?: string;
}) => useQ(keys.activities(p), () => api("/activities", S.ActivityPage, { params: p }));
export const useActivity = (id: string) =>
  useQ(keys.activity(id), () => api(`/activities/${encodeURIComponent(id)}`, S.ActivityDetail), !!id);
export const usePlan = (from?: string, to?: string) =>
  useQ(keys.plan(from, to), () => api("/plan", z.array(S.PlannedSession), { params: { from, to } }));
export const usePhases = () => useQ(keys.phases, () => api("/plan/phases", z.array(S.Phase)));
export const useAdjustments = () =>
  useQ(keys.adjustments, () =>
    api("/plan/adjustments", z.array(S.PlanAdjustment), { params: { limit: 20 } }),
  );
export const useConstraints = () =>
  useQ(keys.constraints, () => api("/plan/constraints", z.array(S.Constraint)));
export const useAvailability = () =>
  useQ(keys.availability, () => api("/plan/availability", z.array(S.Availability)));
export const useGoals = () => useQ(keys.goals, () => api("/goals", z.array(S.Goal)));
export const useRoutes = (day: string) =>
  useQ(keys.routes(day), () => api("/routes", z.array(S.RouteSummary), { params: { day } }), !!day);
export const useRoute = (id: string | null) =>
  useQ(
    keys.route(id ?? ""),
    () => api(`/routes/${encodeURIComponent(id ?? "")}`, S.RouteDetail, { timeoutMs: SLOW_MS }),
    !!id,
  );
export const usePlaces = () => useQ(keys.places, () => api("/places", z.array(S.Place)));
/** Places by name for picking a start place (the server geocodes and caches). */
export const usePlaceSearch = (q: string) =>
  useQuery({
    queryKey: ["place-search", q],
    queryFn: () => api("/places/search", z.array(S.PlaceHit), { params: { q } }),
    enabled: q.trim().length >= 2,
    staleTime: 10 * 60_000,
    // the server answers 503 at once when the geocoder is down: one quick retry, then "Search isn't working"
    retry: (n) => n < 1,
    retryDelay: 800,
  });
export const useBody = (days: number) =>
  useRangeQ(keys.health("body", days), () => api("/health/body", z.array(S.BodyPoint), { params: { days } }));
export const useBp = (days: number) =>
  useRangeQ(keys.health("bp", days), () => api("/health/bp", z.array(S.BpPoint), { params: { days } }));
export const useWellness = (days: number) =>
  useRangeQ(keys.health("wellness", days), () =>
    api("/health/wellness", z.array(S.WellnessPoint), { params: { days } }),
  );
/** Connections; polls every 4 s while a sync is running so progress is live. */
export const useConnections = () =>
  useQuery({
    queryKey: keys.connections,
    queryFn: () => api("/connections", z.array(S.Connection)),
    staleTime: 2_000,
    refetchInterval: (q) => (q.state.data?.some((c) => c.syncing) ? 4_000 : REFRESH),
  });
/** Is a Garmin link (official or the owner's own) active? Then rides arrive by themselves. */
export function useGarminLinked(): boolean {
  const q = useConnections();
  return (q.data ?? []).some(
    (c) => (c.provider === "garmin_api" || c.provider === "garmin") && c.status === "active",
  );
}
export const useHeartRateDay = (day: string) =>
  useQ(keys.heartRate(day), () => api("/health/heart-rate", S.HeartRateDay, { params: { day } }), !!day);
export const useSystemHealth = () => useQ(["system-health"], () => api("/system/health", S.SystemHealth));
export const useRunSelfCheck = () =>
  useM(
    () => api("/system/health/check", S.SystemHealth, { method: "POST", timeoutMs: SLOW_MS }),
    ["system-health"],
  );
export const useSpeedModel = () => useQ(["speed-model"], () => api("/speed-model", S.SpeedModel));
export const useAccount = () => useQ(["account"], () => api("/account", S.Account));
export const useChangePassword = () =>
  useM(
    (v: { current: string | null; new: string }) => apiVoid("/account/password", { method: "POST", body: v }),
    ["account"],
  );
export const useEndOtherSessions = () =>
  useM(
    () => api("/account/sessions/end-others", z.object({ ended: z.number() }), { method: "POST" }),
    ["account"],
  );
export const useEndSession = () =>
  useM((id: number) => apiVoid(`/account/sessions/${id}`, { method: "DELETE" }), ["account"]);
export const useSetConsent = () =>
  useM(
    (v: { kind: "health" | "ai" | "terms"; granted: boolean }) =>
      api("/account/consents", S.Account.shape.consents, { method: "PUT", body: v }),
    // Health and Today read the consent through /me and /status (G3: "Health data is off" right after turning on)
    // RD-16 (T8): readiness and the fitness charts on Progress use health data too
    ["account", "me", "status", "health", "readiness", "coach-note", "amr-fitness", "fitness"],
  );
export const useDeleteAccount = () =>
  useM(
    (v: { confirm_email: string; password: string | null }) =>
      apiVoid("/account/delete", { method: "POST", body: v }),
    [],
  );
export const useCoachNote = () =>
  useQ(["coach-note"], () => api("/coach/note", S.CoachNote, { timeoutMs: SLOW_MS }));
export const useChatSend = () =>
  useM(
    (v: { message: string; conversation_id?: string | null }) =>
      api("/coach/chat", S.ChatReply, { method: "POST", body: v, timeoutMs: SLOW_MS }),
    ["billing"],
  );
export const useBilling = () => useQ(["billing"], () => api("/billing", S.Billing));
export const useCheckout = () =>
  useM(
    (price_key: string) =>
      api("/billing/checkout", S.CheckoutSession, { method: "POST", body: { price_key } }),
    [],
  );
export const usePortal = () =>
  useM((_: void) => api("/billing/portal", z.object({ url: z.string() }), { method: "POST" }), []);
export const useSuggestions = () => useQ(["suggestions"], () => api("/me/suggestions", S.Suggestions));
export const useApplySuggestions = () =>
  useM(
    (fields: S.Suggestion["field"][]) =>
      api("/me/suggestions/apply", S.Suggestions, { method: "POST", body: { fields } }),
    ["suggestions", "me", "settings", "availability", "indoor", "amr-fitness", "phases", ...PLANNISH],
  );
export const useDismissSuggestions = () =>
  useM((_: void) => api("/me/suggestions/dismiss", S.Suggestions, { method: "POST" }), ["suggestions"]);
export const useOnboarding = () => useQ(["onboarding"], () => api("/onboarding", S.Onboarding));
export const useRacePresets = () => useQ(["race-presets"], () => api("/race-presets", z.array(S.RacePreset)));
export type OnboardingAnswer = {
  goal:
    | { placeholder: true; month: string | null } // "No race yet": no name or date
    | {
        name: string;
        event_date: string;
        distance_km?: number | null;
        elevation_m?: number | null;
        days?: number | null;
        profile: {
          preset: string | null;
          distance_km?: number;
          elevation_m?: number | null;
          days?: number;
          unpaved?: number;
          surface?: string;
        };
      };
  week: S.Availability[];
  indoor_months: number[];
  trainer_app: string | null;
  block_weeks: boolean;
  heart_rate: { lthr?: number; max_hr?: number } | null;
};
export const useApplyOnboarding = () =>
  useM(
    (v: OnboardingAnswer) =>
      api("/onboarding", z.object({ goal: S.Goal }), { method: "POST", body: v, timeoutMs: SLOW_MS }),
    [],
  );
export const useInviteRequests = () =>
  useQ(["invite-requests"], () => api("/admin/invite-requests", z.array(S.InviteRequest)));
export const useInviteFromRequest = () =>
  useM(
    (id: number) =>
      api(
        `/admin/invite-requests/${id}/invite`,
        z.object({ status: z.string(), code: z.string(), mailed: z.boolean(), link: z.string() }),
        { method: "POST" },
      ),
    ["invite-requests", "invites"],
  );
export const useAnalytics = () =>
  useQ(["analytics"], () => api("/admin/analytics", S.Analytics, { params: { days: 30 } }));
export const useInvites = () => useQ(["invites"], () => api("/admin/invites", z.array(S.Invite)));
export const useCreateInvite = () =>
  useM(
    (v: { note: string; max_uses: number; days: number; email?: string | null }) =>
      api("/admin/invites", S.Invite, { method: "POST", body: v }),
    ["invites"],
  );
export const useAdminUsers = () => useQ(["admin-users"], () => api("/admin/users", S.AdminUsers));
/** 0.9.12: running the beta — reports, the problem list, one rider's setup (admins only) */
export const useFeedbackList = () => useQ(["admin-feedback"], () => api("/admin/feedback", S.FeedbackList));
export const useProblems = (days = 7) =>
  useQ(["admin-problems", days], () => api("/admin/problems", S.Problems, { params: { days } }));
export const useRiderSupport = (id: string | null) =>
  useQuery({
    queryKey: ["admin-support", id],
    queryFn: () => api(`/admin/users/${encodeURIComponent(id ?? "")}/support`, S.RiderSupport),
    enabled: !!id,
  });
export function useSendFeedback() {
  return useMutation({
    mutationFn: (body: { text: string; context?: Record<string, string> | null }) =>
      api("/feedback", S.FeedbackCreated, { body }),
  });
}
export function useSetFeedbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: "open" | "done" }) =>
      api(`/admin/feedback/${id}`, S.FeedbackItem, { method: "PUT", body: { status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-feedback"] }),
  });
}
export const useSuspendUser = () =>
  useM(
    (v: { id: string; suspend: boolean }) =>
      api(`/admin/users/${encodeURIComponent(v.id)}/${v.suspend ? "suspend" : "unsuspend"}`, S.AdminUser, {
        method: "POST",
      }),
    ["admin-users"],
  );
export const useGarminAccess = () =>
  useM(
    (v: { id: string; allowed: boolean }) =>
      api(`/admin/users/${encodeURIComponent(v.id)}/garmin-login`, S.AdminUser, {
        method: "POST",
        body: { allowed: v.allowed },
      }),
    ["admin-users"],
  );
export const useResetLink = () =>
  useM(
    (id: string) => api(`/admin/users/${encodeURIComponent(id)}/reset-link`, S.ResetLink, { method: "POST" }),
    [],
  );
export const useSetTimezone = () =>
  useM(
    (timezone: string) => api("/me/timezone", S.Me, { method: "PUT", body: { timezone } }),
    ["account", "me", "status", "plan", "readiness", "inbox", "weeks", "indoor", "routes"],
  );
export const useRevokeAccess = () =>
  useM(
    (_: void) => api("/account/revoke-access", S.RevokeAccess, { method: "POST" }),
    ["account", "tokens", "mcp"],
  );
export const useDeleteInvite = () =>
  useM((id: number) => apiVoid(`/admin/invites/${id}`, { method: "DELETE" }), ["invites"]);
export const usePush = () => useQ(["push"], () => api("/push", S.PushInfo));
export const useAddPushDevice = () =>
  useM(
    (body: {
      endpoint: string;
      keys: { p256dh: string; auth: string };
      device: string;
      morning_time: string;
    }) => api("/push/devices", S.PushDevice, { method: "POST", body }),
    ["push"],
  );
export const useSetPushTime = () =>
  useM(
    (v: { id: number; morning_time: string }) =>
      api(`/push/devices/${v.id}`, S.PushDevice, { method: "PATCH", body: { morning_time: v.morning_time } }),
    ["push"],
  );
export const useRemovePushDevice = () =>
  useM((id: number) => apiVoid(`/push/devices/${id}`, { method: "DELETE" }), ["push"]);
export const useTestPush = () =>
  useM(
    (id: number) => api(`/push/devices/${id}/test`, z.object({ ok: z.boolean() }), { method: "POST" }),
    ["push"],
  );
export const useStorage = () => useQ(keys.storage, () => api("/system/storage", S.StorageReport));
export const useMcpConnector = () => useQ(keys.mcp, () => api("/mcp-connector", S.McpConnector));
export const useCreateMcpConnector = () =>
  useM((_: void) => api("/mcp-connector", S.McpConnectorUrl, { method: "POST" }), ["mcp"]);
export const useCatalog = () => useQ(keys.catalog, () => api("/events/catalog", z.array(S.CatalogEvent)));
export const useRecommendations = () =>
  useQ(keys.recommendations, () => api("/events/recommendations", z.array(S.EventRecommendation)));
export const useSettings = () => useQ(keys.settings, () => api("/me/settings", S.Settings));
export const useIndoor = (day: string) =>
  useQ(keys.indoor(day), () => api("/indoor", S.Indoor, { params: { day } }), !!day);
export const useTokens = () => useQ(keys.tokens, () => api("/tokens", z.array(S.Token)));

/** Mutation helper that invalidates the given query-key prefixes on success. */
function useM<V, R>(fn: (v: V) => Promise<R>, invalidate: string[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // 503 = the server was busy and did nothing (overload protection, docs/deploy.md): try again
    retry: (n, e) => e instanceof ApiError && e.status === 503 && n < 2,
    retryDelay: (n) => 1500 * (n + 1),
    onSuccess: () => Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

const PLANNISH = ["plan", "status", "weeks", "adjustments", "routes", "fitness"]; // fitness: the CTL target line (RD-16)

export const useUpdateSession = () =>
  useM(
    (v: {
      id: number;
      patch: Partial<
        Pick<
          S.PlannedSession,
          "day" | "kind" | "sport" | "target_hours" | "hr_cap" | "description" | "status"
        >
      >;
    }) => api(`/plan/sessions/${v.id}`, S.PlannedSession, { method: "PATCH", body: v.patch }),
    PLANNISH,
  );
export const useCreateSession = () =>
  useM(
    (v: {
      day: string;
      kind: S.SessionKind;
      sport: string;
      target_hours: number;
      description?: string;
      hr_cap?: number | null;
    }) => api("/plan/sessions", S.PlannedSession, { body: v }),
    PLANNISH,
  );
export const useDeleteSession = () =>
  useM((id: number) => apiVoid(`/plan/sessions/${id}`, { method: "DELETE" }), PLANNISH);
export const useRegeneratePlan = () =>
  useM(
    (_: void) => api("/plan/regenerate", S.RegenerateResult, { method: "POST", timeoutMs: SLOW_MS }),
    [...PLANNISH, "phases"],
  );
export const useCreateConstraint = () =>
  useM(
    (v: Omit<S.Constraint, "id">) => api("/plan/constraints", S.Constraint, { body: v }),
    ["constraints", ...PLANNISH],
  );
export const useDeleteConstraint = () =>
  useM(
    (id: number) => apiVoid(`/plan/constraints/${id}`, { method: "DELETE" }),
    ["constraints", ...PLANNISH],
  );
export const useSaveAvailability = () =>
  useM(
    (rows: S.Availability[]) =>
      api("/plan/availability", z.array(S.Availability), { method: "PUT", body: rows }),
    ["availability", ...PLANNISH],
  );
const GOALISH = ["goals", "status", "me", "recommendations", "phases", "indoor", "amr-fitness", ...PLANNISH];
export const useSaveGoal = () =>
  useM(
    (g: Partial<S.Goal> & { id?: string; catalog_id?: string | null }) =>
      g.id
        ? api(`/goals/${g.id}`, S.Goal, { method: "PATCH", body: { ...g, id: undefined } })
        : api("/goals", S.Goal, { body: g }),
    GOALISH,
  );
export const useDeleteGoal = () =>
  useM((id: string) => apiVoid(`/goals/${id}`, { method: "DELETE" }), GOALISH);
export const useSaveSettings = () =>
  useM(
    (v: Partial<S.Settings>) => api("/me/settings", S.Settings, { method: "PUT", body: v }),
    ["settings", "indoor", "amr-fitness", ...PLANNISH],
  );

export const useChooseRoute = () =>
  useM(
    (id: string) => api(`/routes/${id}/choose`, S.RouteSummary, { method: "POST" }),
    [...PLANNISH, "route", "places"], // choosing a described route applies its targets: session, start, plan log
  );
export const useDeclineFtpResult = () =>
  useM(
    (session_id: number) => apiVoid("/plan/ftp-result/decline", { method: "POST", body: { session_id } }),
    ["inbox", "status", "adjustments"],
  );
export const useSwapDays = () =>
  useM(
    (v: { a: string; b: string; weather?: boolean }) =>
      api("/plan/swap-days", z.array(S.PlannedSession), { body: v }),
    [...PLANNISH, "inbox", "route"],
  );
export const useDeclineSwap = () =>
  useM(
    (day: string) => apiVoid("/plan/swap-days/decline", { method: "POST", body: { day } }),
    ["inbox", "status", "adjustments"],
  );
export const useSnoozeFoundRace = () =>
  useM((_: void) => apiVoid("/coach/found-race/snooze", { method: "POST" }), ["inbox", "status"]);
export const useDismissRoute = () =>
  useM((id: string) => api(`/routes/${id}/dismiss`, S.RouteSummary, { method: "POST" }), ["routes", "route"]);
export const useGenerateRoutes = () =>
  useM(
    (v: { day?: string; place_id?: string; target_hours?: number }) =>
      api("/routes/generate", z.array(S.RouteSummary), { body: v, timeoutMs: SLOW_MS }),
    ["routes", "route"],
  );

export const useAddPlace = () =>
  useM((p: Omit<S.Place, "id">) => api("/places", S.Place, { body: p }), ["places"]);
export const useUpdatePlace = () =>
  useM(
    (p: Partial<S.Place> & { id: string }) => api(`/places/${p.id}`, S.Place, { method: "PATCH", body: p }),
    ["places"],
  );
export const useDeletePlace = () =>
  useM((id: string) => apiVoid(`/places/${id}`, { method: "DELETE" }), ["places"]);
export const useSetDayStart = () =>
  useM(
    (v: { day: string; place_id: string | null }) =>
      api(`/day-start/${v.day}`, S.DayStart, { method: "PUT", body: { place_id: v.place_id } }),
    ["routes"],
  );

export const useUpload = () =>
  useM(
    (v: { file: File; day?: string; kind?: "ride" | "route" }) => {
      const form = new FormData();
      form.append("file", v.file);
      if (v.day) form.append("day", v.day);
      if (v.kind) form.append("kind", v.kind);
      return api("/uploads", S.UploadResult, { form, method: "POST" });
    },
    ["activities", "status", "weeks", "fitness", "amr-fitness", "routes", "health", "plan", "uploads"],
  );
/** Background imports and load recalculation; polls every 3 s while something is running. */
export const useUploads = () =>
  useQuery({
    queryKey: keys.uploads,
    queryFn: () => api("/uploads", S.UploadList),
    staleTime: 2_000,
    refetchInterval: (q) => {
      const d = q.state.data;
      const busy = d?.recalculating || d?.items.some((i) => i.status === "queued" || i.status === "running");
      return busy ? 3_000 : REFRESH;
    },
  });

// RD-16 (T8): a weight or blood pressure changes readiness and the race-fitness card on Progress
const HEALTHISH = ["health", "status", "readiness", "amr-fitness"];
export const useLogBp = () =>
  useM(
    (v: {
      systolic: number;
      diastolic: number;
      pulse?: number | null;
      note?: string | null;
      measured_at?: string;
    }) => api("/health/bp", S.BpPoint, { body: v }),
    HEALTHISH,
  );
export const useDeleteBp = () =>
  useM((id: string | number) => apiVoid(`/health/bp/${id}`, { method: "DELETE" }), HEALTHISH);
export const useLogBody = () =>
  useM(
    (v: { weight_kg: number; body_fat_pct?: number | null; measured_at?: string }) =>
      api("/health/body", S.BodyPoint, { body: v }),
    HEALTHISH,
  );

export const useSaveThresholds = () =>
  useM(
    (v: {
      lthr?: number | null;
      max_hr?: number | null;
      resting_hr?: number | null;
      ftp_w?: number | null;
      valid_from?: string;
    }) => api("/me/thresholds", S.RecomputeResult, { method: "PUT", body: v, timeoutMs: SLOW_MS }),
    [
      "me",
      "status",
      "activities",
      "activity",
      "readiness",
      "fitness",
      "weeks",
      "inbox",
      "amr-fitness",
      "plan",
      "uploads",
    ],
  );

export const useSync = () =>
  useM((_: void) => api("/sync", S.SyncStart, { method: "POST" }), ["connections"]);
export const useKomootLogin = () =>
  useM(
    (v: { email: string; password: string }) =>
      api("/connections/komoot/login", S.KomootLoginResult, { body: v, timeoutMs: SLOW_MS }),
    ["connections", "komoot"],
  );
export const useKomootTours = (enabled: boolean) =>
  useQ(
    ["komoot", "tours"],
    () => api("/komoot/tours", z.array(S.KomootTour), { timeoutMs: SLOW_MS }),
    enabled,
  );
/** The preview step of "Use": fetches the tour and the diff to the plan; saves nothing. */
export const useKomootPreview = (id: string | null, day: string) =>
  useQuery({
    queryKey: ["komoot", "preview", id ?? "", day],
    queryFn: () =>
      api(`/komoot/tours/${encodeURIComponent(id ?? "")}/preview`, S.KomootPreview, {
        params: { day },
        timeoutMs: SLOW_MS,
      }),
    enabled: !!id,
    staleTime: Infinity, // one slow call to Komoot per look: no interval, no refetch on focus
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
export const useKomootImport = () =>
  useM(
    (v: { id: string; day: string }) =>
      api(`/komoot/tours/${v.id}/import`, S.RouteSummary, { body: { day: v.day }, timeoutMs: SLOW_MS }),
    ["routes", "route", "komoot", ...PLANNISH],
  );
export const useGarminLogin = () =>
  useM(
    (v: { email: string; password: string }) =>
      api("/connections/garmin/login", S.GarminLoginResult, { body: v, timeoutMs: SLOW_MS }),
    ["connections", "status"],
  );
export const useGarminMfa = () =>
  useM(
    (v: { login_id: string; code: string }) =>
      api("/connections/garmin/mfa", S.GarminLoginResult, { body: v, timeoutMs: SLOW_MS }),
    ["connections", "status"],
  );
export const useDisconnect = () =>
  useM(
    (provider: string) => apiVoid(`/connections/${provider}`, { method: "DELETE" }),
    ["connections", "status"],
  );
export const useConnectionOptions = () =>
  useM(
    (v: { provider: string; options: Record<string, boolean> }) =>
      api(`/connections/${v.provider}/options`, S.Connection, {
        method: "PUT",
        body: { options: v.options },
      }),
    ["connections"],
  );
export const useCreateToken = () =>
  useM(
    (v: { name: string; kind: "personal" | "device" }) => api("/tokens", S.NewToken, { body: v }),
    ["tokens"],
  );
export const useRevokeToken = () =>
  useM((id: string | number) => apiVoid(`/tokens/${id}`, { method: "DELETE" }), ["tokens"]);
export const usePairDevice = () =>
  useM((v: { name: string }) => api("/devices/pair", S.Pairing, { body: v }), ["tokens"]);

// ---------------------------------------------------------------- v0.5: the coach conversation
export type MorningInput = Partial<
  Pick<
    S.Checkin,
    "legs" | "energy" | "soreness" | "motivation" | "sleep_quality" | "ill" | "injured" | "note"
  >
> & { day?: string; bp_systolic?: number | null; bp_diastolic?: number | null; bp_pulse?: number | null };
export type RideInput = Partial<
  Pick<S.Checkin, "rpe" | "legs" | "stomach" | "carbs_g" | "fluids_l" | "loaded" | "note">
>;

export const useSkipReasons = () =>
  useQuery({
    queryKey: keys.skipReasons,
    queryFn: () => api("/plan/skip-reasons", z.array(S.SkipReason)),
    staleTime: Infinity,
  });
export const useMorningCheckin = () =>
  useM(
    (v: MorningInput) => api("/checkins/morning", S.MorningResult, { method: "PUT", body: v }),
    [...PLANNISH, "inbox", "readiness", "health"], // a blood pressure entered with the check-in (G3)
  );
export const useRideCheckin = () =>
  useM(
    (v: { activityId: string; data: RideInput }) =>
      api(`/activities/${encodeURIComponent(v.activityId)}/checkin`, S.RideCheckinResult, {
        method: "PUT",
        body: v.data,
      }),
    ["activity", "activities", "status", "inbox", "readiness", "race-practice"],
  );
export const useSkipSession = () =>
  useM(
    (v: { id: number; reason: S.SkipReason["key"]; note: string }) =>
      api(`/plan/sessions/${v.id}/skip`, S.SkipResult, {
        method: "POST",
        body: { reason: v.reason, note: v.note },
      }),
    [...PLANNISH, "inbox"],
  );
export const useRouteRequest = (day: string) =>
  useQ(
    keys.routeRequest(day),
    () => api("/routes/request", S.RouteRequest.nullable(), { params: { day } }),
    !!day,
  );
export const useAskRoute = () =>
  useM(
    (v: { text: string }) =>
      api("/routes/request", S.RouteRequestResult, { method: "POST", body: v, timeoutMs: SLOW_MS }),
    [...PLANNISH, "route", "places", "indoor"],
  );

export const useRacePractice = () => useQ(keys.racePractice, () => api("/race-practice", S.RacePractice));
export const useAmrFitness = (days = 365) =>
  useRangeQ(keys.amrFitness(days), () => api("/amr-fitness", S.AmrFitness, { params: { days } }));
